import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getProductPerformance } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge, NdoeImage,
  NdoeStats, NdoeStatCard, NdoeError,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import useRefLists from './useRefLists';
import ReportFilters from './ReportFilters';
import { money, days, pct, rate, dateTxt, CONFIANCE_COURT } from './format';
import './rapports.css';

const paliersBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.palier_tone} label={row.palier_label} />
);
const confianceBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.confiance_tone} label={CONFIANCE_COURT[row.confiance] || row.confiance_label} />
);

// Pointures effectivement vendues, en pastilles compactes.
const pointuresVendues = (row) => (row.pointures || []).filter((p) => p.vendu > 0);

const SORTS = [
  { value: 'quantite', label: 'Quantité vendue' },
  { value: 'ca', label: "Chiffre d'affaires" },
  { value: 'cadence', label: 'Cadence (paires/mois)' },
  { value: 'ecoulement', label: "Taux d'écoulement" },
  { value: 'commandes', label: 'Nombre de commandes' },
];
const STATUTS = [
  { value: '', label: 'Tous les stocks' },
  { value: 'rupture', label: 'En rupture (0)' },
  { value: 'faible', label: 'Stock faible (≤ 2)' },
  { value: 'ok', label: 'Stock confortable' },
];

const PROFILS = [
  { value: '', label: 'Tous les profils' },
  { value: 'incomplet', label: 'Profil pointures incomplet' },
];

const EMPTY_FILTERS = { marque: '', categorie: '', matiere: '', couleur: '', pointure: '', statut: '', profil: '' };

// État d'une pointure dans le tiroir : rupture réclamée (avec ventes manquées),
// épuisée sans demande, en vente, ou en stock sans vente.
const etatPointure = (p, manquantes) => {
  const m = (manquantes || []).find((x) => x.taille === p.taille);
  if (m) return { tone: 'danger', label: `Rupture · ≈ ${m.ventes_manquees} manquée(s)` };
  if (p.vendu > 0 && p.stock <= 0) return { tone: 'neutral', label: 'Épuisée, demande éteinte' };
  if (p.vendu > 0) return { tone: 'ok', label: 'Se vend' };
  if (p.stock > 0) return { tone: 'info', label: 'En stock, ne se vend pas' };
  return { tone: 'neutral', label: '—' };
};

export default function PerformanceProduits() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  // Arrivée depuis le rapport Ventes (clic marque/catégorie) : pré-filtrage URL.
  const [searchParams] = useSearchParams();
  const [period, setPeriod] = useState({ period: 30 });
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    marque: Number(searchParams.get('marque')) || '',
    categorie: Number(searchParams.get('categorie')) || '',
  }));
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('quantite');
  const [page, setPage] = useState(1);

  const [payload, setPayload] = useState({ meta: {}, summary: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const { openFiche, ficheElement } = useFicheArticle();

  const refs = useRefLists(isAdmin);

  // Recherche débattue (évite une requête par frappe).
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [q]);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
    ...filters, q: search, sort,
  }), [period, filters, search, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProductPerformance({ ...apiParams, page, page_size: 20 });
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport de performance produits.');
    } finally { setLoading(false); }
  }, [apiParams, page]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { setPage(1); }, [period, filters, sort]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value ?? '' }));
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  // --- Export CSV (toutes les lignes filtrées, pas seulement la page) ---------
  const handleExport = async () => {
    setExporting(true);
    try {
      const all = [];
      let current = 1;
      let total = Infinity;
      while (all.length < total && current <= 25) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await getProductPerformance({ ...apiParams, page: current, page_size: 60 });
        total = data.meta?.total ?? all.length;
        all.push(...(data.data || []));
        if (!data.data?.length) break;
        current += 1;
      }
      const columns = [
        { title: 'Produit', value: (r) => r.nom_produit },
        { title: 'Référence', value: (r) => r.code_produit ?? '' },
        { title: 'Marque', value: (r) => r.marque },
        { title: 'Catégorie', value: (r) => r.categorie },
        { title: 'Matière', value: (r) => r.matiere },
        { title: 'Couleurs', value: (r) => (r.couleurs || []).join(', ') },
        { title: 'Pointures vendues', value: (r) => pointuresVendues(r).map((p) => `P${p.taille}:${p.vendu}`).join(' ') },
        { title: 'Profil incomplet', value: (r) => (r.profil_incomplet ? 'Oui' : '') },
        { title: 'Pointures manquantes', value: (r) => (r.pointures_manquantes || []).map((p) => `P${p.taille} (≈${p.ventes_manquees})`).join(' ') },
        { title: 'Ventes manquées (est.)', value: (r) => r.ventes_manquees || '' },
        { title: 'Quantité vendue', value: (r) => r.quantite_vendue },
        { title: "Chiffre d'affaires", value: (r) => r.chiffre_affaires },
        { title: 'Commandes', value: (r) => r.nombre_commandes },
        { title: 'Coût unitaire estimé', value: (r) => r.cout_unitaire ?? '' },
        { title: 'Marge estimée', value: (r) => r.marge_estimee ?? '' },
        { title: 'Taux de marge (%)', value: (r) => r.taux_marge ?? '' },
        { title: 'Confiance coût', value: (r) => r.cout_confiance ?? '' },
        { title: 'Stock actuel', value: (r) => r.stock_actuel },
        { title: 'Cadence (paires/mois)', value: (r) => r.cadence_mensuelle },
        { title: "Taux d'écoulement (%)", value: (r) => r.taux_ecoulement },
        { title: 'Couverture (jours)', value: (r) => r.couverture_jours ?? '' },
        { title: 'Délai FIFO moyen (j)', value: (r) => r.delai_fifo_jours ?? '' },
        { title: 'Délai FIFO min (j)', value: (r) => r.delai_fifo_min ?? '' },
        { title: 'Délai FIFO max (j)', value: (r) => r.delai_fifo_max ?? '' },
        { title: 'Première vente', value: (r) => dateTxt(r.premiere_vente) },
        { title: 'Dernière vente', value: (r) => dateTxt(r.derniere_vente) },
        { title: 'Palier', value: (r) => r.palier_label },
        { title: 'Confiance donnée', value: (r) => r.confiance_label },
        { title: 'Recommandation', value: (r) => r.recommandation_label },
      ];
      const { start_date: from, end_date: to } = payload.meta || {};
      downloadCsv(`performance-produits_${from || ''}_${to || ''}.csv`, toCsv(columns, all));
      toast.success(`${all.length} produit(s) exporté(s).`);
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const { summary = {}, meta = {}, data = [] } = payload;

  const columns = [
    {
      title: 'Photo', dataIndex: 'img', width: 76,
      render: (img, r) => <NdoeImage src={img} alt={r.nom_produit} size="md" preview={false} />,
    },
    {
      title: 'Produit', dataIndex: 'nom_produit',
      render: (nom, r) => (
        <div className="pp-produit">
          <b>{nom || '—'}</b>
          <small>{[r.marque, r.categorie, r.matiere].filter(Boolean).join(' · ') || '—'}</small>
          {r.code_produit && <small className="num pp-ref">{r.code_produit}</small>}
          {pointuresVendues(r).length > 0 && (
            <div className="pp-pointures">
              {pointuresVendues(r).map((p) => (
                <span className="pp-pt" key={p.taille}>P{p.taille}<i>{p.vendu}</i></span>
              ))}
            </div>
          )}
          {r.profil_incomplet && <NdoeStatusBadge kind="custom" tone="warn" label="Profil incomplet" />}
          {r.deconseille && <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />}
        </div>
      ),
    },
    { title: 'Vendu', dataIndex: 'quantite_vendue', align: 'right', width: 82, render: (v) => <span className="num">{v}</span> },
    { title: 'CA', dataIndex: 'chiffre_affaires', align: 'right', width: 108, render: (v) => <span className="num">{money(v)}</span> },
    {
      // Marge estimée via la cascade de coût. Coût inconnu → « — », jamais un
      // chiffre inventé (même principe que le délai FIFO).
      title: 'Marge (est.)', dataIndex: 'marge_estimee', align: 'right', width: 118,
      render: (v, r) => (v === null || v === undefined
        ? <span className="num pp-marge--inconnue">—</span>
        : (
          <div className="pp-marge">
            <b className={`num ${v < 0 ? 'is-neg' : ''}`}>{money(v)}</b>
            <small className="num">{r.taux_marge} %{r.cout_confiance === 'estime' || r.cout_confiance === 'rattrapage' ? ' · est.' : ''}</small>
          </div>
        )),
    },
    { title: 'Cadence', dataIndex: 'cadence_mensuelle', align: 'right', width: 100, render: (v) => <span className="num">{rate(v)}</span> },
    { title: 'Écoulement', dataIndex: 'taux_ecoulement', align: 'right', width: 106, render: (v) => <span className="num">{pct(v)}</span> },
    { title: 'Stock', dataIndex: 'stock_actuel', align: 'right', width: 80, render: (v) => <span className="num">{v}</span> },
    { title: 'Couverture', dataIndex: 'couverture_jours', align: 'right', width: 106, render: (v) => <span className="num">{days(v)}</span> },
    { title: 'Délai FIFO', dataIndex: 'delai_fifo_jours', align: 'right', width: 100, render: (v) => <span className="num">{days(v)}</span> },
    { title: 'Palier', dataIndex: 'palier', width: 132, render: (_, r) => paliersBadge(r) },
    { title: 'Confiance', dataIndex: 'confiance', width: 126, render: (_, r) => confianceBadge(r) },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id_produit}
      media={<NdoeImage src={r.img} alt={r.nom_produit} size="lg" preview={false} />}
      title={r.nom_produit || '—'}
      subtitle={[r.marque, r.categorie].filter(Boolean).join(' · ')}
      badge={paliersBadge(r)}
      metrics={[
        { label: 'Vendu', value: r.quantite_vendue },
        { label: 'CA', value: money(r.chiffre_affaires) },
        { label: 'Écoulement', value: pct(r.taux_ecoulement) },
        { label: 'Couverture', value: days(r.couverture_jours) },
      ]}
      onClick={() => setDetail(r)}
    />
  );

  const KpiCard = ({ label, value, gold, onClick }) => (
    onClick ? (
      <div className="pp-kpi-click" role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <NdoeStatCard label={label} value={value} gold={gold} />
      </div>
    ) : <NdoeStatCard label={label} value={value} gold={gold} />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Performance Produits"
        subtitle="Ce qui se vend, ce qui tourne vite, et ce qu'il faut recommander — sur 7, 30, 90 ou 180 jours."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && (
          <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>
        )}
      </div>

      <NdoeStats className="pp-stats">
        <KpiCard label="Paires vendues" value={summary.paires_vendues ?? 0} />
        <KpiCard label="Chiffre d'affaires" value={money(summary.chiffre_affaires)} gold />
        <KpiCard
          label={summary.marge_couverture_pct ? `Marge (sur ${summary.marge_couverture_pct} % du CA)` : 'Marge estimée'}
          value={summary.marge_couverture_pct ? money(summary.marge_estimee) : '—'}
        />
        <KpiCard label="Écoulement moyen" value={pct(summary.taux_ecoulement_moyen)} />
        <KpiCard label="Très performants" value={summary.tres_performants ?? 0} />
        <KpiCard label="En rupture" value={summary.en_rupture ?? 0} onClick={() => setFilter('statut')('rupture')} />
        <KpiCard label="Profils incomplets" value={summary.profils_incomplets ?? 0}
          onClick={() => setFilter('profil')('incomplet')} />
        <KpiCard label="À recommander" value={summary.a_recommander ?? 0} />
      </NdoeStats>

      {summary.profils_incomplets > 0 && (
        <p className="pp-note">
          <NdoeStatusBadge kind="custom" tone="warn" label="Profil incomplet" />
          {' '}{summary.profils_incomplets} modèle(s) continuent de vendre alors qu'une de leurs pointures
          est à zéro&nbsp;: ≈&nbsp;<b>{summary.ventes_manquees_estimees}</b> vente(s) sans doute manquée(s)
          sur la période (estimation prudente, jamais comptée quand la demande du modèle s'est éteinte).
        </p>
      )}

      {summary.donnees_estimees > 0 && (
        <p className="pp-note">
          <NdoeStatusBadge kind="custom" tone="warn" label="Date entrée estimée" />
          {' '}{summary.donnees_estimees} produit(s) n'ont pas de réception tracée&nbsp;: leur délai FIFO n'est pas
          calculé (affiché «&nbsp;—&nbsp;»). Les autres indicateurs restent fiables.
        </p>
      )}

      {summary.marge_couverture_pct !== undefined && summary.marge_couverture_pct < 100 && (
        <p className="pp-note">
          <NdoeStatusBadge kind="custom" tone={summary.marge_couverture_pct === 0 ? 'neutral' : 'info'} label="Marge" />
          {' '}La marge n'est calculée que sur <b>{summary.marge_couverture_pct} %</b> du chiffre d'affaires —
          les produits sans coût d'achat en sont exclus, jamais estimés à zéro. Complète les coûts dans
          «&nbsp;Réceptions&nbsp;→&nbsp;Coûts d'achat&nbsp;» (saisie ou rattrapage en masse).
        </p>
      )}

      <ReportFilters
        q={q} setQ={setQ}
        filters={filters} setFilter={setFilter}
        refs={refs}
        sort={sort} setSort={setSort} sorts={SORTS}
        activeCount={activeCount}
      >
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Statut stock" style={{ minWidth: 165 }} options={STATUTS}
            value={filters.statut} onChange={setFilter('statut')} />
        </div>
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Profil pointures" style={{ minWidth: 190 }} options={PROFILS}
            value={filters.profil} onChange={setFilter('profil')} />
        </div>
      </ReportFilters>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : (
        <NdoeResponsiveTable
          className="pp-table"
          data={data}
          columns={columns}
          renderCard={renderCard}
          rowKey="id_produit"
          loading={loading}
          error={error}
          onRetry={load}
          scroll={{ x: 1180 }}
          onRow={(record) => ({ onClick: () => setDetail(record), style: { cursor: 'pointer' } })}
          pagination={{ current: meta.page || 1, pageSize: meta.page_size || 20, total: meta.total || 0, showSizeChanger: false }}
          onChange={(pagination) => setPage(pagination?.current || 1)}
          empty={{
            icon: '📊',
            title: 'Aucun produit vendu sur cette période',
            text: 'Élargis la période ou retire des filtres.',
          }}
        />
      )}

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={520}
        title={detail ? <NdoeArticleLink onClick={() => openFiche(detail.id_produit)}>{detail.nom_produit}</NdoeArticleLink> : ('' || 'Détail produit')}
        className="pp-drawer"
      >
        {detail && (
          <div className="pp-detail">
            <div className="pp-detail__head">
              <NdoeImage src={detail.img} alt={detail.nom_produit} size="hero" />
              <div className="pp-detail__meta">
                <b>{[detail.marque, detail.categorie].filter(Boolean).join(' · ') || '—'}</b>
                <small>{detail.matiere || 'Matière non renseignée'}</small>
                {detail.code_produit && <small className="num">Réf. {detail.code_produit}</small>}
                <small>{(detail.couleurs || []).join(', ') || 'Couleur non renseignée'}</small>
                <div className="pp-detail__badges">
                  {paliersBadge(detail)}
                  {confianceBadge(detail)}
                  {detail.deconseille && <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />}
                </div>
              </div>
            </div>

            <NdoeStats>
              <NdoeStatCard label="Vendu" value={detail.quantite_vendue} />
              <NdoeStatCard label="Chiffre d'affaires" value={money(detail.chiffre_affaires)} gold />
              <NdoeStatCard label="Commandes" value={detail.nombre_commandes} />
              <NdoeStatCard label="Stock actuel" value={detail.stock_actuel} />
              <NdoeStatCard label="Cadence" value={rate(detail.cadence_mensuelle)} />
              <NdoeStatCard label="Écoulement" value={pct(detail.taux_ecoulement)} />
            </NdoeStats>

            <h4 className="pp-detail__title">Marge (coût d'achat)</h4>
            {detail.marge_estimee === null || detail.marge_estimee === undefined ? (
              <p className="pp-detail__warn">
                Aucun coût d'achat connu pour ce produit&nbsp;: la marge n'est pas calculée (jamais estimée à zéro).
                Saisis le coût dans «&nbsp;Réceptions&nbsp;→&nbsp;Coûts d'achat&nbsp;».
              </p>
            ) : (
              <p className="pp-detail__reco">
                Coût unitaire <b>{money(detail.cout_unitaire)}</b>
                {(detail.cout_confiance === 'estime' || detail.cout_confiance === 'rattrapage') && ' (estimé)'}
                {' '}→ marge <b>{money(detail.marge_estimee)}</b> sur la période, soit <b>{detail.taux_marge} %</b> du CA.
              </p>
            )}

            <h4 className="pp-detail__title">Décision recommandée</h4>
            <p className="pp-detail__reco">
              <b>{detail.recommandation_label}</b>
              {detail.couverture_jours !== null && (
                <> — au rythme actuel, le stock restant tient environ <b>{detail.couverture_jours} jours</b>.</>
              )}
              {detail.deconseille && detail.motif_deconseille && (
                <><br /><span className="pp-detail__motif">Motif du déconseil&nbsp;: {detail.motif_deconseille}</span></>
              )}
            </p>

            <h4 className="pp-detail__title">Rotation (FIFO entrée → vente)</h4>
            {detail.delai_fifo_jours === null ? (
              <p className="pp-detail__warn">
                Aucune réception tracée pour ce produit&nbsp;: le délai FIFO n'est pas calculé. La date d'entrée
                serait estimée à partir de la création de la fiche, ce qui donnerait un délai trompeur.
              </p>
            ) : (
              <p>
                Délai moyen <b>{days(detail.delai_fifo_jours)}</b>
                {detail.delai_fifo_min !== null && <> · min {days(detail.delai_fifo_min)} · max {days(detail.delai_fifo_max)}</>}
              </p>
            )}

            <h4 className="pp-detail__title">Profil pointures</h4>
            {detail.profil_incomplet && (
              <p className="pp-detail__warn">
                Ce modèle vend encore, mais certaines pointures réclamées sont à zéro&nbsp;:
                ≈&nbsp;<b>{detail.ventes_manquees}</b> vente(s) sans doute manquée(s) sur la période
                (estimation prudente, bornée à 2× ce que la pointure avait vendu).
              </p>
            )}
            <div className="pp-detail__pt">
              <div className="pp-detail__pt-row pp-pt4-row pp-detail__pt-row--head">
                <span>Pointure</span><span>Vendu</span><span>Stock</span><span>État</span>
              </div>
              {(detail.pointures || []).map((p) => {
                const etat = etatPointure(p, detail.pointures_manquantes);
                return (
                  <div className="pp-detail__pt-row pp-pt4-row" key={p.taille}>
                    <span>P{p.taille}</span>
                    <span className="num">{p.vendu}</span>
                    <span className="num">{p.stock}</span>
                    <span><NdoeStatusBadge kind="custom" tone={etat.tone} label={etat.label} /></span>
                  </div>
                );
              })}
              {!(detail.pointures || []).length && <p className="pp-detail__warn">Aucune pointure renseignée.</p>}
            </div>

            <h4 className="pp-detail__title">Chronologie</h4>
            <ul className="pp-detail__dates">
              <li><span>Première vente</span><b>{dateTxt(detail.premiere_vente)}</b></li>
              <li><span>Dernière vente</span><b>{dateTxt(detail.derniere_vente)}</b></li>
              <li><span>Entrée en catalogue</span><b>{dateTxt(detail.date_entrant)}</b></li>
            </ul>
          </div>
        )}
      </Drawer>
      {ficheElement}
    </NdoePage>
  );
}
